import type { NamedExoticComponent, PropsWithChildren } from "react";
import { memo, useMemo } from "react";
import clsx from "clsx";

import { Subtitle } from "../subtitle";

import classes from "./styles.module.scss";

import type {
  IButtonProps,
  IColumnProps,
  IFullWidthColumnProps,
  IGridProps,
} from ".";

const Grid = memo(function Grid(props: PropsWithChildren<IGridProps>) {
  const {
    children,
    flowDense,
    className,
    containerClassName,
    noMarginBottom,
    noMarginTop,
    subtitle,
    border,
  } = props;

  const subtitleComponent = useMemo(
    () =>
      subtitle && (
        <Subtitle
          title={subtitle}
          className={clsx([classes.fullWidthColumn, "pb-4m1"])}
        />
      ),
    [subtitle],
  );

  return (
    <div
      className={clsx([
        classes.container,
        containerClassName,
        noMarginTop && classes.nmt,
        noMarginBottom && classes.nmb,
        border === "bottom-gradient" && ["border-gr-bottom", "mb-0", "pb-4m1"],
        border === "top-gradient" && ["border-gr-top", "mt-0", "pt-4m1"],
        border === "bottom-solid" && ["border-bottom", "mb-0", "pb-4m1"],
        border === "top-solid" && ["border-top", "mt-0", "pt-4m1"],
      ])}
    >
      {subtitleComponent}
      <div
        className={clsx([
          classes.grid,
          flowDense && classes.gridFlowDense,
          className,
        ])}
      >
        {children}
      </div>
    </div>
  );
}) as NamedExoticComponent<PropsWithChildren<IGridProps>> & {
  Column: NamedExoticComponent<PropsWithChildren<IColumnProps>>;
  Button: NamedExoticComponent<PropsWithChildren<IButtonProps>>;
  Question: NamedExoticComponent<PropsWithChildren<IButtonProps>>;
  FullWidthColumn: NamedExoticComponent<
    PropsWithChildren<IFullWidthColumnProps>
  >;
};

const Column = memo(function Column(props: PropsWithChildren<IColumnProps>) {
  const {
    children,
    doubleWidth,
    tripleWidth,
    startFromNextRow,
    gap,
    className,
  } = props;

  return (
    <div
      className={clsx([
        tripleWidth ? classes.tripleWidth : doubleWidth && classes.bigColumn,
        startFromNextRow && classes.startFromNextRow,
        gap && classes.gap20,
        className,
      ])}
    >
      {children}
    </div>
  );
});

const FullWidthColumn = memo(function FullWidthColumn(
  props: PropsWithChildren<IFullWidthColumnProps>,
) {
  const { className, children } = props;

  return (
    <div className={clsx([classes.fullWidthColumn, className])}>{children}</div>
  );
});

const Button = memo(function Button(props: PropsWithChildren<IButtonProps>) {
  const { className, children } = props;

  return (
    <div className={clsx([classes.gridButton, className])}>{children}</div>
  );
});

const Question = memo(function Question(
  props: PropsWithChildren<IButtonProps>,
) {
  const { className, children } = props;

  return (
    <div className={clsx([classes.gridQuestion, className])}>{children}</div>
  );
});

Grid.Column = Column;
Grid.Button = Button;
Grid.Question = Question;
Grid.FullWidthColumn = FullWidthColumn;
export { Grid };
